import { api } from '@/services/api/AxiosBaseService'
import { createCrudService } from '@/services/api/createCrudService'

export interface CommissionStructureRule {
  id: string
  commissionStructureId: string
  installmentFrom: number
  installmentTo: number | null
  installmentAction: 'A PARTIR DE' | 'INTERVALO'
  value: number
  commissionType: 'VALOR FIXO' | 'PORCENTAGEM'
  /** Legado — não preenchido pelo form atual. Default backend: 'VENDEDOR'. */
  userType?: 'DIRETOR' | 'SUPERVISOR' | 'AFILIADO' | 'VENDEDOR'
  formatted?: { value?: string }
}

export interface CommissionStructure {
  id: string
  name: string
  /** `null` = sem limite de parcelas. */
  monthlyInstallments: number | null
  /** `null` = sem meta de vendas. */
  monthlyPlansNeedSold: number | null
  status: 'ATIVO' | 'INATIVO'
  rules?: CommissionStructureRule[]
  formatted?: { createdAt?: string }
}

/**
 * Fábrica do serviço de estruturas de comissão comercial. O mesmo CRUD é usado
 * pelo admin (`/commercial/...`, estruturas globais) e pelo parceiro
 * (`/partner/...`, estruturas privadas dele).
 */
export function makeCommissionStructuresService(basePath: string) {
  const base = createCrudService<CommissionStructure>(basePath, { searchField: 'name' })
  return {
    ...base,
    async listRules(structureId: string): Promise<CommissionStructureRule[]> {
      const r = await api.get<{ data: CommissionStructureRule[] }>(`${basePath}/${structureId}/rules`)
      return r.data.data
    },
    async createRule(structureId: string, payload: Omit<CommissionStructureRule, 'id' | 'commissionStructureId' | 'formatted'>): Promise<CommissionStructureRule> {
      const r = await api.post<{ data: CommissionStructureRule }>(`${basePath}/${structureId}/rules`, payload)
      return r.data.data
    },
    async updateRule(structureId: string, ruleId: string, payload: Partial<CommissionStructureRule>): Promise<CommissionStructureRule> {
      const r = await api.put<{ data: CommissionStructureRule }>(`${basePath}/${structureId}/rules/${ruleId}`, payload)
      return r.data.data
    },
    async destroyRule(structureId: string, ruleId: string): Promise<void> {
      await api.delete(`${basePath}/${structureId}/rules/${ruleId}`)
    },
  }
}

export type CommissionStructuresApi = ReturnType<typeof makeCommissionStructuresService>

/** Admin — estruturas globais. */
export const CommissionStructuresService = makeCommissionStructuresService('/commercial/commission-structures')

/** Parceiro — estruturas privadas (owner = parceiro logado). */
export const PartnerCommissionStructuresService = makeCommissionStructuresService('/partner/commission-structures')
